import { useTranslator } from '@bluebeam/react-i18n';
import { BannerContext } from 'components/Routes/BannerContext';
import FileSelectNoticeModal from 'components/modals/FileSelectNoticeModal';
import {
  StdList,
  StdListBody,
  StdListHeader,
  StdListHeaderCell,
} from 'components/StdList';
import React, { CSSProperties, FC, useContext, useEffect } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { ModalState } from 'records/ModalState';
import { ProcessingStatus } from 'records/ProcessingStatus';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { UploadsRenderer } from './UploadsRenderer';

const gridTemplateColumns =
  '1.2fr 1.5fr minmax(118px, 0.5fr) minmax(210px, 1fr)';

interface RenderingProps {
  style: CSSProperties;
  index: number;
  data: ProcessingStatus[];
}

interface StyleProps {
  height: number;
  width: number;
}

interface Props extends RouteComponentProps, WithAppStoreProps {
  location: {
    pathname: string;
    hash: string;
    search: string;
    state: {
      unsupportedFiles: boolean;
    };
  };
}

const UploadsList: FC<Props> = ({ actions, appState, location }) => {
  const { translate } = useTranslator(['uploadsList', 'toastText']);
  const { height: bannerHeight } = useContext(BannerContext);
  const processingStatuses = appState.processingStatuses
    .sortBy((ps) => -ps.uploadTimestamp)
    .valueSeq()
    .toArray();
  const itemHeight = 65;

  const launchFileSelectNoticeModal = (): void => {
    const modalState = new ModalState({
      id: 'fileSelectionNoticeModal',
      portal: 'main',
      open: true,
      props: {
        handleClose: () => actions.clearAllModals(),
      },
      component: FileSelectNoticeModal,
    });

    actions.createModal(modalState);
  };

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      actions.getRecentUploads();
      const toastText = {
        fileUploadError: {
          title: translate('toastText:fileUploadError.title'),
          message: translate('toastText:fileUploadError.message'),
        },
        drawingsReadyForReview: {
          title: translate('toastText:drawingsReadyForReview.title'),
          message: translate('toastText:drawingsReadyForReview.message'),
        },
        fileUploadSuccess: {
          title: translate('toastText:fileUploadSuccess.title'),
          message: translate('toastText:fileUploadSuccess.message'),
        },
      };
      actions.getProcessingStatuses(toastText);
    }, 20000);
    if (location?.state?.unsupportedFiles) {
      launchFileSelectNoticeModal();
    }
    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  return (
    <div style={{ height: `calc(100vh - 153px - ${bannerHeight}px)` }}>
      <StdList
        id="drawings-uploads-list"
        style={{ overflow: 'hidden' }}
        gridTemplateColumns={gridTemplateColumns}
      >
        <StdListHeader>
          <StdListHeaderCell
            text={translate('uploadsList:uploadDate')}
            leftBorder={false}
          />
          <StdListHeaderCell text={translate('uploadsList:uploadedBy')} />
          <StdListHeaderCell text={translate('uploadsList:pageCount')} />
          <StdListHeaderCell text={translate('uploadsList:status')} />
        </StdListHeader>
        <StdListBody enableScrolling={false}>
          <AutoSizer>
            {({ height, width }: StyleProps) => (
              <FixedSizeList
                height={height}
                width={width}
                itemCount={processingStatuses.length}
                itemSize={itemHeight}
                itemData={processingStatuses}
              >
                {({ style, index, data }: RenderingProps) => (
                  <UploadsRenderer style={style} index={index} data={data} />
                )}
              </FixedSizeList>
            )}
          </AutoSizer>
        </StdListBody>
      </StdList>
    </div>
  );
};

export default withRouter(withAppStore(UploadsList));
